import { useEffect, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

export default function AdminOndemandAreas() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/merchant/ondemand/areas");
      setData(d);
    } catch (e) {
      toast.error(extractErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/merchant/ondemand/areas", data);
      setData(d);
      toast.success("Service area saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const zones = data.zones || [];
  const setZone = (i, patch) => {
    const next = [...zones];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, zones: next });
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-ondemand-areas">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Ondemand service area</h1>
        <p className="text-sm text-slate-500 mt-1">Pincodes, ETA minutes, packing copy, and delay messages. Shoppers outside these pins see a coming-soon screen.</p>
      </div>
      <Card className="p-5 space-y-4">
        <div>
          <Label>Coming soon message</Label>
          <Input className="mt-1" value={data.coming_soon_message || ""} onChange={(e) => setData({ ...data, coming_soon_message: e.target.value })} data-testid="od-coming-soon" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Store label on tracking</Label>
            <Input className="mt-1" value={data.store_label || ""} onChange={(e) => setData({ ...data, store_label: e.target.value })} />
          </div>
          <div>
            <Label>Home label on tracking</Label>
            <Input className="mt-1" value={data.home_label || ""} onChange={(e) => setData({ ...data, home_label: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Packing messages (max 5, shown in first 5 minutes)</Label>
          <Textarea className="mt-1" rows={4} value={(data.prep_messages || []).join("\n")} onChange={(e) => setData({ ...data, prep_messages: e.target.value.split("\n") })} data-testid="od-prep-msgs" />
        </div>
        <div>
          <Label>Late messages (max 5)</Label>
          <Textarea className="mt-1" rows={4} value={(data.delay_messages || []).join("\n")} onChange={(e) => setData({ ...data, delay_messages: e.target.value.split("\n") })} data-testid="od-delay-msgs" />
        </div>
      </Card>
      <Card className="p-5 space-y-4">
        <div className="font-semibold">Pincode zones</div>
        {zones.map((z, i) => (
          <div key={i} className="rounded-xl border p-3 space-y-2" data-testid={`od-zone-${i}`}>
            <div className="grid sm:grid-cols-[1fr_120px] gap-2">
              <div>
                <Label className="text-xs">Pincodes (space or comma)</Label>
                <Input className="mt-1" value={(z.pins || []).join(" ")} onChange={(e) => setZone(i, { pins: e.target.value.split(/[\s,]+/) })} />
              </div>
              <div>
                <Label className="text-xs">ETA minutes</Label>
                <Input type="number" className="mt-1" value={z.eta_minutes || 30} onChange={(e) => setZone(i, { eta_minutes: Number(e.target.value) })} />
              </div>
            </div>
            <Input placeholder="Label" value={z.label || ""} onChange={(e) => setZone(i, { label: e.target.value })} />
            <button type="button" className="text-xs text-red-600" onClick={() => setData({ ...data, zones: zones.filter((_, j) => j !== i) })}>Remove zone</button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" data-testid="od-zone-add" onClick={() => setData({ ...data, zones: [...zones, { pins: [], eta_minutes: 30, label: "" }] })}>+ Add zone</Button>
      </Card>
      <Button onClick={save} disabled={saving} className="bg-slate-900" data-testid="od-areas-save">{saving ? "Saving…" : "Save"}</Button>
    </div>
  );
}
